'use client';
import { cx } from '@linaria/core';
import type { JSX, JSXElementConstructor, ReactNode } from 'react';

import {
	dropdownElement,
	DropdownElementHint,
	DropdownElementSecondaryIcon,
} from './styles';

type HTMLTag = keyof JSX.IntrinsicElements;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AsType = HTMLTag | JSXElementConstructor<any>;

export type DropdownElementProps<T extends AsType = 'button'> = {
	closeOnClick?: boolean;
	className?: string;
	icon?: ReactNode;
	secondaryIcon?: ReactNode;
	hint?: ReactNode;
	as?: T;
} & (T extends HTMLTag
	? JSX.IntrinsicElements[T]
	: T extends JSXElementConstructor<infer P>
	? P
	: never);

export function DropdownElement<T extends AsType = 'button'>(
	props: DropdownElementProps<T>
): JSX.Element;

export function DropdownElement({
	className,
	closeOnClick = true,
	icon,
	secondaryIcon,
	hint,
	as: As = 'button',
	children,
	...restProps
}: DropdownElementProps<'button'>) {
	return (
		<As
			data-coc={closeOnClick ? true : undefined}
			className={cx(dropdownElement, className)}
			role="menuitem"
			{...restProps}
		>
			{icon}
			{hint ? (
				<div>
					{children}
					<DropdownElementHint>{hint}</DropdownElementHint>
				</div>
			) : (
				children
			)}
			{secondaryIcon && (
				<DropdownElementSecondaryIcon>
					{secondaryIcon}
				</DropdownElementSecondaryIcon>
			)}
		</As>
	);
}
